import { SignInRequired } from "../../../auth/signIn";
import React from "react";
import { PlusOutlined, BarChartOutlined } from "@ant-design/icons";
import {
    Button, Card, Col, Form, Input, List, Modal, Popconfirm, Row, Select, Space,
    Typography,
} from "antd";
import { ErrorState, LoadingState, PageHeader, StatTile } from "@dowty/ui";
import { AccountDataSection } from "./AccountDataSection";
import { PushSettings } from "./PushSettings";
import { EventVisibility } from "../../../generated/drinks_pb";
import dayjs from "dayjs";
import { useProfile } from "./useProfile";
import "../AnalyticsPages.css";

const { Text } = Typography;
type ProfileState = ReturnType<typeof useProfile>;

export const ProfileView: React.FC<{ state: ProfileState }> = ({ state }) => {
    const {
        isAuthenticated, user, navigate, isMobile, t, visibilityOptions, totalDrinks, lastDrinkAt,
        loading, profileError, loadProfile, eventsLoading, creatingEvent, activeEvent, openEvents, form, accessForm,
        accessModalVisible, updatingAccess, passwordPrompt, passwordPromptValue, setPasswordPromptValue,
        selectedVisibility, selectedAccessVisibility, deletingEvent, loadEvents, handleCreateEvent,
        handleJoinEvent, handleLeaveEvent, handleEndEvent, handleDeleteEvent, openAccessModal,
        closeAccessModal, handleUpdateEventAccess, renderVisibilityTag, handlePasswordPromptConfirm,
        handlePasswordPromptCancel,
    } = state;
    if (!isAuthenticated || !user) {
        return <SignInRequired title={t("profile.authRequired.title")} description={t("profile.authRequired.description")} />;
    }

    const errorView = profileError ? <ErrorState message={profileError} retry={() => { void loadProfile(); }} retryLabel={t("recap.retry")} /> : null;
    return (
        <div className="analytics-page profile-page">
            <PageHeader
                title={t("profile.hero.greeting", { name: user.username })}
                subtitle={t("profile.hero.subtitle")}
                extra={(
                    <Space wrap className="profile-header-actions">
                        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => navigate("/drinks/add")}>
                            {t("profile.cta.addDrink")}
                        </Button>
                        <Button icon={<BarChartOutlined />} size="large" onClick={() => navigate("/drinks/stats")}>
                            {t("profile.cta.viewStats")}
                        </Button>
                    </Space>
                )}
            />


            {loading ? <LoadingState variant="stats" label={t("profile.loading")} /> : errorView ?? <section className="profile-section" aria-labelledby="profile-stats-heading">
                <h2 id="profile-stats-heading">{t("profile.stats.sectionTitle")}</h2>
                <Row gutter={[12, 12]} className="profile-summary">
                <Col xs={12} md={12}>
                    <StatTile label={t("profile.stats.totalVolume")} value={totalDrinks.toLocaleString(state.i18n.resolvedLanguage ?? state.i18n.language)} unit="ml" />
                </Col>
                <Col xs={12} md={12}>
                    <StatTile label={t("profile.stats.lastDrink")} value={lastDrinkAt ? dayjs(lastDrinkAt).format("DD MMM YYYY HH:mm") : t("profile.stats.lastDrinkEmpty")} />
                </Col>
                </Row>
            </section>}

            <Space direction="vertical" size="large" className="profile-sections">
            <section className="profile-section" aria-labelledby="profile-events-heading">
            <h2 id="profile-events-heading">{t("profile.eventsSectionTitle")}</h2>
            <Card
                title={t("profile.currentEvent.title")}
                extra={<Button onClick={loadEvents}>{t("profile.currentEvent.refresh")}</Button>}
                loading={eventsLoading}
            >
                {activeEvent ? (
                    <Space direction="vertical" style={{ width: "100%" }}>
                        <Text strong>{activeEvent.eventName}</Text>
                        <Space size="small" align="center">
                            {renderVisibilityTag(activeEvent)}
                            {activeEvent.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD && (
                                <Text type="secondary">{t("profile.currentEvent.passwordRequired")}</Text>
                            )}
                        </Space>
                        <Text type="secondary">
                            {activeEvent.location || t("profile.currentEvent.locationUnknown")}
                        </Text>
                        <Text>
                            {activeEvent.start
                                ? t("profile.currentEvent.startedLabel", {
                                      time: dayjs(activeEvent.start).format("DD MMM YYYY HH:mm"),
                                  })
                                : t("profile.currentEvent.startedUnknown")}
                        </Text>
                        {activeEvent.end && !activeEvent.isActive && (
                            <Text>
                                {t("profile.currentEvent.endedLabel", {
                                    time: dayjs(activeEvent.end).format("DD MMM YYYY HH:mm"),
                                })}
                            </Text>
                        )}
                        <Space wrap style={{ width: "100%" }}>
                            <Button onClick={handleLeaveEvent} block={isMobile}>
                                {t("profile.currentEvent.leaveButton")}
                            </Button>
                            {activeEvent.ownerUserId === user.userId && (
                                <>
                                    <Button onClick={openAccessModal} block={isMobile}>
                                        {t("profile.currentEvent.manageAccess")}
                                    </Button>
                                    {activeEvent.isActive && (
                                        <Popconfirm
                                            title={t("profile.currentEvent.endConfirmTitle")}
                                            okText={t("profile.currentEvent.endConfirmOk")}
                                            cancelText={t("profile.currentEvent.endConfirmCancel")}
                                            onConfirm={handleEndEvent}
                                        >
                                            <Button danger block={isMobile}>
                                                {t("profile.currentEvent.endButton")}
                                            </Button>
                                        </Popconfirm>
                                    )}
                                    <Popconfirm
                                        title={t("profile.currentEvent.deleteConfirmTitle")}
                                        description={t("profile.currentEvent.deleteConfirmDescription")}
                                        okText={t("profile.currentEvent.deleteConfirmOk")}
                                        okButtonProps={{ danger: true }}
                                        onConfirm={handleDeleteEvent}
                                    >
                                        <Button danger loading={deletingEvent} block={isMobile}>
                                            {t("profile.currentEvent.deleteButton")}
                                        </Button>
                                    </Popconfirm>
                                </>
                            )}
                        </Space>
                    </Space>
                ) : (
                    <Text type="secondary">{t("profile.currentEvent.empty")}</Text>
                )}
            </Card>

            <Card title={t("profile.createEvent.title")}>
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{ visibility: EventVisibility.EVENT_VISIBILITY_PUBLIC }}
                    onFinish={handleCreateEvent}
                >
                    <Form.Item
                        label={t("profile.createEvent.form.eventNameLabel")}
                        name="eventName"
                        rules={[{ required: true, message: t("profile.createEvent.form.eventNameRequired") }, { validator: (_rule, value) => value?.trim() ? Promise.resolve() : Promise.reject(new Error(t("profile.createEvent.form.eventNameRequired"))) }]}
                    >
                        <Input
                            placeholder={t("profile.createEvent.form.eventNamePlaceholder")}
                            maxLength={100}
                        />
                    </Form.Item>
                    <Form.Item label={t("profile.createEvent.form.locationLabel")} name="location">
                        <Input placeholder={t("profile.createEvent.form.locationPlaceholder")} maxLength={100} />
                    </Form.Item>
                    <Form.Item
                        label={t("profile.createEvent.form.visibilityLabel")}
                        name="visibility"
                        required={false}
                        extra={t("common.required")}
                        rules={[{ required: true, message: t("profile.createEvent.form.visibilityRequired") }]}
                    >
                        <Select
                            options={visibilityOptions.map(({ value, label }) => ({ value, label }))}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const currentVisibility = selectedVisibility ?? EventVisibility.EVENT_VISIBILITY_PUBLIC;
                            const requiresPassword = currentVisibility === EventVisibility.EVENT_VISIBILITY_PASSWORD;
                            return (
                                <Form.Item
                                    label={t("profile.createEvent.form.passwordLabel")}
                                    name="password"
                                    rules={
                                        requiresPassword
                                            ? [{ required: true, message: t("profile.createEvent.form.passwordRequired") }]
                                            : []
                                    }
                                    hidden={!requiresPassword}
                                >
                                    <Input.Password
                                        placeholder={t("profile.createEvent.form.passwordPlaceholder")}
                                        maxLength={64}
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={creatingEvent}>
                            {t("profile.createEvent.form.submit")}
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <Card title={t("profile.openEvents.title")} loading={eventsLoading}>
                {openEvents.length === 0 ? (
                    <Text type="secondary">{t("profile.openEvents.empty")}</Text>
                ) : (
                    <List
                        itemLayout="vertical"
                        dataSource={openEvents}
                        renderItem={(item) => {
                            const mustLeaveFirst = Boolean(activeEvent && activeEvent.eventId !== item.eventId);

                            return (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="join"
                                            type="link"
                                            disabled={mustLeaveFirst}
                                            onClick={() => handleJoinEvent(item)}
                                        >
                                            {t("profile.openEvents.joinButton")}
                                        </Button>,
                                    ]}
                                >
                                    <div className="profile-open-event-copy">
                                        <Text strong className="profile-open-event-name">{item.eventName}</Text>
                                        <div>{item.location || t("profile.openEvents.locationFallback")}</div>
                                        <Space size="small" align="center">
                                            {renderVisibilityTag(item)}
                                            {item.visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD && (
                                                <Text type="secondary">{t("profile.currentEvent.passwordRequired")}</Text>
                                            )}
                                        </Space>
                                        {item.start && (
                                            <div>
                                                {t("profile.openEvents.startedLabel", {
                                                    time: dayjs(item.start).format("DD MMM YYYY HH:mm"),
                                                })}
                                            </div>
                                        )}
                                        {mustLeaveFirst && <Text type="secondary">{t("profile.openEvents.leaveCurrentFirst")}</Text>}
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </Card>
            </section>

            <section className="profile-section" aria-labelledby="profile-account-heading">
            <h2 id="profile-account-heading">{t("profile.accountSectionTitle")}</h2>
            <AccountDataSection user={user} />
            <PushSettings user={user} />
            </section>

            <Modal
                title={t("profile.accessModal.title")}
                open={accessModalVisible}
                onOk={handleUpdateEventAccess}
                onCancel={closeAccessModal}
                okText={t("profile.accessModal.save")}
                confirmLoading={updatingAccess}
                destroyOnHidden
            >
                <Form layout="vertical" form={accessForm}>
                    <Form.Item
                        label={t("profile.accessModal.visibilityLabel")}
                        name="visibility"
                        required={false}
                        extra={t("common.required")}
                        rules={[{ required: true, message: t("profile.accessModal.visibilityRequired") }]}
                    >
                        <Select
                            options={visibilityOptions.map(({ value, label }) => ({ value, label }))}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const visibility =
                                selectedAccessVisibility ?? EventVisibility.EVENT_VISIBILITY_PUBLIC;
                            const requiresPassword =
                                visibility === EventVisibility.EVENT_VISIBILITY_PASSWORD;
                            return (
                                <Form.Item
                                    label={t("profile.accessModal.passwordLabel")}
                                    name="password"
                                    rules={
                                        requiresPassword
                                            ? [{ required: true, message: t("profile.accessModal.passwordRequired") }]
                                            : []
                                    }
                                    hidden={!requiresPassword}
                                >
                                    <Input.Password
                                        placeholder={t("profile.accessModal.passwordPlaceholder")}
                                        maxLength={64}
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>
            </Space>
            <Modal
                title={
                    passwordPrompt
                        ? t("profile.passwordModal.title", { event: passwordPrompt.event.eventName })
                        : t("profile.passwordModal.defaultTitle")
                }
                open={!!passwordPrompt}
                okText={t("profile.passwordModal.okText")}
                cancelText={t("profile.passwordModal.cancelText")}
                onOk={handlePasswordPromptConfirm}
                onCancel={handlePasswordPromptCancel}
                destroyOnHidden
            >
                <Input.Password
                    autoFocus
                    placeholder={t("profile.passwordModal.placeholder")}
                    maxLength={64}
                    value={passwordPromptValue}
                    onChange={(e) => setPasswordPromptValue(e.target.value)}
                    onPressEnter={handlePasswordPromptConfirm}
                />
            </Modal>
        </div>
    );
};
